import { Download } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { downloadReport, REPORTS } from '@/lib/admin-api';

/**
 * CSV exports.
 *
 * Rendered on the server rather than from what is on screen - deliberately different
 * from CargoFL Performance, which exports the rows the page already has. These
 * reports are wider than any table here (the register carries price, vendor and
 * invoice number) and must cover every row, not the 25 on the current page. An export
 * that silently gave you one page of a 900-asset register would be worse than none.
 */
export function ReportsPage() {
  const [busy, setBusy] = React.useState<string | null>(null);

  const download = async (name: string, title: string) => {
    setBusy(name);
    try {
      await downloadReport(name);
      toast.success(`${title} downloaded.`);
    } catch {
      toast.error('That report could not be downloaded.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer>
      <p className="text-sm text-muted-foreground">
        Every report is a CSV that opens straight in Excel, covering all rows rather than
        the page you happen to be looking at.
      </p>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {REPORTS.map((report) => (
          <Card key={report.name} className="flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{report.title}</CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                {report.blurb}
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-auto pt-0">
              <Button hint={"Download this report as a spreadsheet"}
                size="sm"
                variant="secondary"
                disabled={busy === report.name}
                onClick={() => void download(report.name, report.title)}
              >
                <Download className="mr-1.5 size-4" />
                {busy === report.name ? 'Preparing…' : 'Download CSV'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </PageContainer>
  );
}
